/* ==========================================================================
   TechNest — theme.js
   Core utilities and the global chrome components: drawers, sticky header,
   mega menu, accordions, localization, scroll reveal and status messaging.

   Everything is a custom element or a delegated listener, so section
   re-renders in the theme editor pick behaviour back up with no extra wiring.
   ========================================================================== */
(function () {
  'use strict';

  const TN = (window.TechNest = window.TechNest || {});
  TN.settings = TN.settings || {};
  TN.strings = TN.strings || {};
  TN.routes = TN.routes || {};

  /* --------------------------------------------------------- colour theme */

  const colourTheme = (TN.colourTheme = {
    get current() {
      return document.documentElement.dataset.tnTheme === 'dark' ? 'dark' : 'light';
    },

    set(theme, persist) {
      const next = theme === 'dark' ? 'dark' : 'light';
      document.documentElement.dataset.tnTheme = next;
      document.documentElement.style.colorScheme = next;

      document.querySelectorAll('[data-tn-theme-toggle]').forEach((button) => {
        const isDark = next === 'dark';
        const label = isDark ? button.dataset.darkLabel : button.dataset.lightLabel;
        button.setAttribute('aria-pressed', String(isDark));
        button.setAttribute('aria-label', label);
        button.title = label;
      });

      const themeMeta = document.querySelector('[data-tn-theme-color]');
      if (themeMeta) themeMeta.content = next === 'dark' ? themeMeta.dataset.dark : themeMeta.dataset.light;

      if (persist !== false) {
        try {
          localStorage.setItem('technest:theme', next);
        } catch (e) {
          /* Storage may be unavailable in strict privacy modes; the toggle still works. */
        }
      }
      document.dispatchEvent(new CustomEvent('tn:theme:change', { detail: { theme: next } }));
    }
  });

  colourTheme.set(colourTheme.current, false);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-tn-theme-toggle]');
    if (!toggle) return;
    colourTheme.set(colourTheme.current === 'dark' ? 'light' : 'dark', true);
  });

  /* ---------------------------------------------------------------- utils */

  const utils = (TN.utils = {
    /** Register a custom element only once — theme editor reloads re-run scripts. */
    define(name, ctor) {
      if (!window.customElements.get(name)) window.customElements.define(name, ctor);
    },

    debounce(fn, wait) {
      let t;
      return function () {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, arguments), wait);
      };
    },

    FOCUSABLE:
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
      'select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',

    focusableWithin(root) {
      return Array.from(root.querySelectorAll(utils.FOCUSABLE)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      );
    },

    /** Keep Tab inside `root` until released. Returns a teardown function. */
    trapFocus(root, initial) {
      const onKeydown = (event) => {
        if (event.key !== 'Tab') return;
        const items = utils.focusableWithin(root);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      root.addEventListener('keydown', onKeydown);
      const target = initial || utils.focusableWithin(root)[0] || root;
      // Wait a frame so the element is visible before focus, or iOS ignores it.
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
      return () => root.removeEventListener('keydown', onKeydown);
    },

    lockScroll() {
      if (document.body.classList.contains('tn-no-scroll')) return;
      const gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) document.body.style.paddingRight = gap + 'px';
      document.body.classList.add('tn-no-scroll');
    },

    unlockScroll() {
      document.body.classList.remove('tn-no-scroll');
      document.body.style.paddingRight = '';
    },

    /** Announce a message to screen readers via the shared live region. */
    announce(message) {
      const region = document.getElementById('TnLiveRegion');
      if (!region || !message) return;
      region.textContent = '';
      // A microtask gap makes repeated identical messages re-announce.
      setTimeout(() => (region.textContent = message), 60);
    },

    /** Visible, non-blocking feedback. Also announced for screen readers. */
    toast(message, type) {
      const el = document.getElementById('TnToast');
      if (!el || !message) return utils.announce(message);
      const text = el.querySelector('[data-tn-toast-text]');
      if (text) text.textContent = message;
      el.classList.toggle('tn-toast--error', type === 'error');
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-open'));
      utils.announce(message);
      clearTimeout(el._timer);
      el._timer = setTimeout(() => utils.hideToast(), 4500);
    },

    hideToast() {
      const el = document.getElementById('TnToast');
      if (!el) return;
      el.classList.remove('is-open');
      clearTimeout(el._timer);
      el._timer = setTimeout(() => (el.hidden = true), 400);
    },

    /** Interpolate the [placeholder] tokens used in the locale strings. */
    format(template, replacements) {
      let out = template || '';
      Object.keys(replacements || {}).forEach((key) => {
        out = out.replace('[' + key + ']', replacements[key]);
      });
      return out;
    },

    /**
     * Fetch markup for one or more sections using Shopify's Section Rendering
     * API, so prices and totals are always formatted server-side by Liquid.
     */
    async fetchSections(sections, url) {
      const base = url || window.location.pathname;
      const query = 'sections=' + sections.join(',');
      const response = await fetch(base + (base.indexOf('?') > -1 ? '&' : '?') + query);
      if (!response.ok) throw new Error('Section fetch failed: ' + response.status);
      return response.json();
    },

    /** Replace an element's inner HTML from a section render response. */
    replaceInner(selector, html) {
      const target = document.querySelector(selector);
      if (!target || html == null) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(selector);
      target.innerHTML = source ? source.innerHTML : html;
    },

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  });

  /* ------------------------------------------------------------ tn-drawer */

  /**
   * Slide-in panel. Handles Escape, focus trapping, scroll locking and
   * returning focus to whatever opened it.
   */
  class TnDrawer extends HTMLElement {
    constructor() {
      super();
      this._onKeydown = this._onKeydown.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
    }

    connectedCallback() {
      this.setAttribute('role', this.getAttribute('role') || 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-tn-drawer-close]')) this.close();
      });
    }

    get overlay() {
      if (!this._overlay) {
        this._overlay = document.querySelector('[data-tn-overlay]');
        if (!this._overlay) {
          this._overlay = document.createElement('div');
          this._overlay.className = 'tn-overlay';
          this._overlay.setAttribute('data-tn-overlay', '');
          document.body.appendChild(this._overlay);
        }
      }
      return this._overlay;
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this._opener = opener || document.activeElement;
      this.hidden = false;
      this.overlay.classList.add('is-open');
      this.overlay.addEventListener('click', this._onOverlayClick);
      // Force a reflow so the transition runs from the closed position.
      void this.offsetWidth;
      this.classList.add('is-open');
      utils.lockScroll();
      document.addEventListener('keydown', this._onKeydown);
      this._release = utils.trapFocus(this, this.querySelector('[data-tn-drawer-focus]'));
      if (this._opener && this._opener.hasAttribute('aria-expanded')) {
        this._opener.setAttribute('aria-expanded', 'true');
      }
      this.dispatchEvent(new CustomEvent('tn:drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.overlay.classList.remove('is-open');
      this.overlay.removeEventListener('click', this._onOverlayClick);
      utils.unlockScroll();
      document.removeEventListener('keydown', this._onKeydown);
      if (this._release) this._release();
      if (this._opener) {
        if (this._opener.hasAttribute('aria-expanded')) {
          this._opener.setAttribute('aria-expanded', 'false');
        }
        if (document.body.contains(this._opener)) this._opener.focus({ preventScroll: true });
      }
      // Keep it out of the tab order once the transition finishes.
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => {
        if (!this.classList.contains('is-open')) this.hidden = true;
      }, 400);
      this.dispatchEvent(new CustomEvent('tn:drawer:close', { bubbles: true }));
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    }

    _onOverlayClick() {
      this.close();
    }
  }
  utils.define('tn-drawer', TnDrawer);
  TN.TnDrawer = TnDrawer;

  // Delegated drawer openers so markup only needs data-tn-drawer-open="<id>".
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-tn-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.getAttribute('data-tn-drawer-open'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* ------------------------------------------------------------ tn-header */

  class TnHeader extends HTMLElement {
    connectedCallback() {
      this._searchToggle = this.querySelector('[data-tn-search-toggle]');
      this._searchPanel = this.querySelector('[data-tn-search-panel]');
      this._searchClose = this.querySelector('[data-tn-search-close]');

      if (this._searchToggle && this._searchPanel) {
        this._searchToggle.addEventListener('click', () => this.toggleSearch());
        if (this._searchClose) this._searchClose.addEventListener('click', () => this.closeSearch(true));
        this._searchPanel.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeSearch(true);
        });
      }

      this._onScroll = utils.debounce(() => {
        this.classList.toggle('is-scrolled', window.scrollY > 8);
      }, 40);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
      this._measure();
      window.addEventListener('resize', (this._onResize = utils.debounce(() => this._measure(), 150)));
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
    }

    /** Publish the real header height so sticky offsets stay accurate. */
    _measure() {
      document.documentElement.style.setProperty('--tn-header-height', this.offsetHeight + 'px');
    }

    toggleSearch() {
      if (this._searchPanel.hidden) this.openSearch();
      else this.closeSearch(true);
    }

    openSearch() {
      this._searchPanel.hidden = false;
      this._searchToggle.setAttribute('aria-expanded', 'true');
      const input = this._searchPanel.querySelector('[data-tn-search-input], input[type="search"]');
      if (input) requestAnimationFrame(() => input.focus());
    }

    closeSearch(restoreFocus) {
      this._searchPanel.hidden = true;
      this._searchToggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this._searchToggle.focus();
    }
  }
  utils.define('tn-header', TnHeader);

  /* ---------------------------------------------------------- tn-mega-nav */

  /**
   * Desktop mega menu. Each item is a real link plus a disclosure button, so
   * the link always navigates and the button always announces its state.
   * Pointer users get hover; keyboard users get click plus Escape.
   */
  class TnMegaNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-tn-mega-item]'));
      this._hoverable = window.matchMedia('(hover: hover) and (min-width: 990px)');

      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-tn-mega-toggle]');
        const panel = item.querySelector('[data-tn-mega-panel]');
        if (!toggle || !panel) return;

        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          const open = toggle.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!open) this.openItem(item);
        });

        item.addEventListener('pointerenter', () => {
          if (!this._hoverable.matches) return;
          clearTimeout(this._closeTimer);
          this.closeAll(item);
          this.openItem(item);
        });

        item.addEventListener('pointerleave', () => {
          if (!this._hoverable.matches) return;
          this._closeTimer = setTimeout(() => this.closeItem(item), 140);
        });

        // Tabbing out of the whole item closes it.
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.closeItem(item);
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const item = event.target.closest('[data-tn-mega-item]');
        if (!item) return;
        const toggle = item.querySelector('[data-tn-mega-toggle]');
        this.closeItem(item);
        if (toggle) toggle.focus();
      });
    }

    openItem(item) {
      const toggle = item.querySelector('[data-tn-mega-toggle]');
      const panel = item.querySelector('[data-tn-mega-panel]');
      if (!toggle || !panel) return;
      panel.classList.add('is-open');
      item.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      this._keepInViewport(panel);
    }

    closeItem(item) {
      const toggle = item.querySelector('[data-tn-mega-toggle]');
      const panel = item.querySelector('[data-tn-mega-panel]');
      if (!toggle || !panel) return;
      panel.classList.remove('is-open');
      item.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      panel.style.removeProperty('left');
    }

    closeAll(except) {
      this.items.forEach((item) => {
        if (item !== except) this.closeItem(item);
      });
    }

    /** Nudge a panel back on-screen if it would overflow the right edge. */
    _keepInViewport(panel) {
      if (panel.classList.contains('tn-mega--full')) return;
      panel.style.removeProperty('left');
      const rect = panel.getBoundingClientRect();
      const overflow = rect.right - (window.innerWidth - 16);
      if (overflow > 0) panel.style.left = -overflow + 'px';
    }
  }
  utils.define('tn-mega-nav', TnMegaNav);

  /* --------------------------------------------------------- accordions */

  /**
   * Generic button + panel accordion (mobile nav, FAQ groups, filters).
   * <details> is used where the content is static; this handles the cases
   * that need scripted state such as "only one open at a time".
   */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-tn-accordion-toggle]');
    if (!toggle) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    event.preventDefault();
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isOpen));
    panel.classList.toggle('is-open', !isOpen);
  });

  /* ------------------------------------------------------- localization */

  // Country and language pickers submit their own native Shopify form.
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-tn-localization-select]');
    if (!select) return;
    const form = select.closest('form');
    if (form) form.submit();
  });

  /* ---------------------------------------------------------- toast close */

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-tn-toast-close]')) utils.hideToast();
  });

  /* --------------------------------------------------------------- sharing */

  // Native share sheet where the browser has one, clipboard copy otherwise.
  document.querySelectorAll('[data-tn-share]').forEach(function (root) {
    const nativeBtn = root.querySelector('[data-tn-share-native]');
    if (nativeBtn && navigator.share) {
      nativeBtn.hidden = false;
      nativeBtn.addEventListener('click', function () {
        navigator
          .share({ title: root.dataset.shareTitle, url: root.dataset.shareUrl })
          .catch(function () {
            /* Shopper dismissed the sheet — nothing to report. */
          });
      });
    }
  });

  document.addEventListener('click', async (event) => {
    const copy = event.target.closest('[data-tn-share-copy]');
    if (!copy) return;
    const root = copy.closest('[data-tn-share]');
    if (!root) return;
    try {
      await navigator.clipboard.writeText(root.dataset.shareUrl);
      utils.toast(TN.strings.linkCopied);
    } catch (e) {
      // Clipboard blocked: select the URL so the shopper can copy it manually.
      const field = document.createElement('input');
      field.value = root.dataset.shareUrl;
      field.setAttribute('readonly', 'readonly');
      field.className = 'tn-input';
      root.appendChild(field);
      field.select();
    }
  });

  /* -------------------------------------------------------- scroll reveal */

  function initReveal(root) {
    if (!TN.settings.animationsEnabled && document.documentElement.classList.contains('tn-animations-off')) return;
    if (utils.prefersReducedMotion() || !('IntersectionObserver' in window)) {
      (root || document).querySelectorAll('.tn-reveal').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    (root || document).querySelectorAll('.tn-reveal:not(.is-visible)').forEach((el) => observer.observe(el));
  }
  TN.initReveal = initReveal;

  /* ----------------------------------------------- external link labelling */

  function labelExternalLinks(root) {
    (root || document).querySelectorAll('a[target="_blank"]:not([aria-describedby])').forEach((link) => {
      link.setAttribute('aria-describedby', 'a11y-new-window-message');
    });
  }

  /* ------------------------------------------------------------- lifecycle */

  function init(root) {
    initReveal(root);
    labelExternalLinks(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Theme editor: re-run initialisers against freshly injected section markup.
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    document.dispatchEvent(new CustomEvent('tn:section:load', { detail: { target: event.target } }));
  });
  document.addEventListener('shopify:section:unload', (event) => {
    document.dispatchEvent(new CustomEvent('tn:section:unload', { detail: { target: event.target } }));
  });
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.querySelector('tn-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
})();
